import type React from 'react';
import { memo, useMemo } from 'react';
import { useSubscription, graphql } from 'react-relay';
import { JiraIssueAri } from '@atlassian/ari/jira/issue';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { isSubscriptionEventType, type RealtimeEvent } from '../../../common/types/events.tsx';
import { SOURCE_SUBSCRIPTIONS } from '../../../common/constants/sources.tsx';

type Props = {
	appId: string;
	projectIds: string[];
	events: string[];
	onJoin: () => void;
	onReceive: (event: RealtimeEvent) => void;
	onLeave: () => void;
	onError: (error: Error) => void;
};

const LOG_LOCATION = 'jira-realtime-issue-subscription';

export const IssueSubscription: React.FC<Props> = memo(
	({ appId, projectIds, events, onJoin, onReceive, onError, onLeave }: Props) => {
		const cloudId = useCloudId();
		const atlassianAccountId = useAccountId();

		const defaultAttributes = {
			source: SOURCE_SUBSCRIPTIONS,
			cloudId,
			appId,
			atlassianAccountId,
		};

		useSubscription(
			useMemo(
				() => ({
					subscription: graphql`
						subscription IssueSubscription(
							$cloudId: ID!
							$atlassianAccountId: String!
							$events: [String!]!
							$projectIds: [String!]!
						) {
							jira {
								onJirtIssueSubscription(
									cloudId: $cloudId
									events: $events
									projectIds: $projectIds
									atlassianAccountId: $atlassianAccountId
								) {
									type
									actionerAccountId
									resource
									project {
										id
									}
								}
							}
						}
					`,
					variables: {
						cloudId,
						atlassianAccountId: String(atlassianAccountId),
						events,
						projectIds,
					},
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					onNext: (response: any) => {
						const payload = response?.jira?.onJirtIssueSubscription;
						if (!payload) {
							log.safeInfoWithoutCustomerData(
								LOG_LOCATION,
								'Subscription started',
								defaultAttributes,
							);
							onJoin();
							return;
						}

						if (!isSubscriptionEventType(payload.type)) {
							log.safeErrorWithoutCustomerData(LOG_LOCATION, 'Invalid event type', {
								...defaultAttributes,
								eventType: payload.type,
							});
							return;
						}

						const issueId = Number(JiraIssueAri.parse(payload.resource).issueId);

						const realtimeEvent: RealtimeEvent = {
							type: payload.type,
							payload: {
								atlassianId: String(payload.actionerAccountId),
								projectId: payload.project?.id,
								issueId,
							},
						};

						log.safeInfoWithoutCustomerData(LOG_LOCATION, 'Event received', {
							...defaultAttributes,
							eventType: payload.type,
							projectId: payload.project?.id,
							issueId,
							resource: payload.resource,
							triggeredBy: String(payload.actionerAccountId),
							receivedBy: atlassianAccountId,
							isTriggeredBySameUser: String(payload.actionerAccountId) === atlassianAccountId,
						});

						onReceive(realtimeEvent);
					},
					onCompleted: () => {
						log.safeInfoWithoutCustomerData(LOG_LOCATION, 'Subscription completed', {
							...defaultAttributes,
							receivedBy: atlassianAccountId,
						});
						onLeave();
					},
					onError: (error: Error) => {
						log.safeErrorWithoutCustomerData(
							LOG_LOCATION,
							`[${appId}] Subscription failure`,
							error,
						);
						onError(error);
					},
				}),
				// Given React hooks require dependencies to be stable, but this cannot be guaranteed by consumers.
				// To ensure the subscription is created only once and not recreated on every re-render,
				// an empty dependency array is used intentionally. This approach ensures stability and avoids unnecessary resubscriptions.
				// eslint-disable-next-line react-hooks/exhaustive-deps
				[],
			),
		);

		return null;
	},
);
